/*
 * kr-ig-price-sync.js  (Kraymer Art theme asset, 25 Sep 2026). Same file on the live theme and the redesign theme.
 *
 * Why this exists
 *   The theme shows the automatic 15% as a display price (Theme settings > "Enable global discount"): the Shopify price
 *   is shown struck and price x (100 - 15)%, rounded up to the cent, is shown as the price. Checkout then applies the
 *   automatic 15%OFF / 20% OFF discounts to the Shopify price.
 *   Intelligems price tests write the test group's raw Shopify-level price P into div[ref="priceContainer"] .price and
 *   hide div[ref="priceContainer"] .compare-at-price (class ig-hidden), because the variant has no Shopify compare-at.
 *   Without this script every tested product shows P with no strike, in BOTH arms.
 *   This script puts every container Intelligems writes back into today's format:
 *     struck compare-at = P,  price = ceil(P x (100 - pct) / 100)  (the same rounding the Liquid uses),
 *   in the store's money format (taken from the Liquid money filter, see data-kr-money on the script tag).
 *
 * Rules
 *   - Only containers the theme renders with data-kr-ig and data-kr-disc="1" are touched. Products tagged
 *     no-sitewide-discount (and gift cards) render data-kr-disc="0" and keep exactly what Intelligems writes.
 *   - Nothing happens until Intelligems writes. With no price test running the script changes nothing.
 *   - Idempotent: our output is kept in data-kr-ig-out and the Intelligems price in data-kr-ig-p, and a value that equals
 *     the Liquid sale price (data-kr-now) is always treated as the untouched Liquid render, so a price is never
 *     discounted twice (also after Horizon re-renders or morphs a container).
 *   - No tug of war with Intelligems (price splitting is on for this store: Intelligems caches each price element's
 *     outerHTML and rewrites the element whenever it differs). After we rewrite a live .price / .compare-at-price we mark
 *     that element with data-variant-id="0" data-product-id="0" (plus data-kr-ig-mark). Intelligems reads ids from the
 *     element itself first, finds no test for variant 0 and leaves the element alone; the container keeps the real ids.
 *     The marks are removed again whenever the element shows the Liquid price (re-render, morph), so Intelligems can
 *     apply the next variant. Writes go through Text.nodeValue (characterData), which Intelligems does not observe.
 *     A breaker stops rewriting a container that changes more than 8 times in 3 s (logged), should that ever fail.
 *   - Two container modes:
 *       inplace  (live Horizon snippets/price.liquid): Intelligems writes into the visible .price / .compare-at-price;
 *                we rewrite those two elements. DOM order is left as Liquid rendered it.
 *       sink     (redesign snippets/kr-price.liquid): Intelligems writes into hidden sink spans; we copy the result into
 *                the visible [data-was] / [data-now]. The visible elements are never Intelligems targets.
 *   - Derived text (SAVE badges, per piece, installments) is only rewritten when P differs from the Shopify price
 *     (data-kr-base), so the control arm keeps exactly what Liquid rendered.
 *   - Anti-flicker: Intelligems keeps its own price targets at opacity 0 while <style id="ig-af-price"> exists. In sink
 *     mode the visible prices are not its targets, so while that style exists we hide them the same way (max 5 s).
 *     In inplace mode our rewrite runs in the microtask right after the Intelligems write, before the next paint.
 *
 * Load: a classic (blocking) script in <head>, placed before the Intelligems bundle, so the observer exists before
 *   Intelligems can write. 17.5 KB as written, about 6 KB gzipped over the wire (4 KB without these comments).
 *   Debug: add ?krig=debug to a URL to log every rewrite to the console.
 */
(function () {
  'use strict';
  if (window.KRIG) return;

  var d = document;
  var root = d.documentElement;
  var me = d.currentScript;
  var cfg = (me && me.dataset) || {};
  var DEBUG = /[?&]krig=debug\b/.test(location.search);
  var SEL = '[data-kr-ig]';
  var T0 = Date.now();
  var AF_MAX_MS = 5000;

  function log() {
    if (DEBUG && window.console) console.log.apply(console, ['[krig]'].concat([].slice.call(arguments)));
  }
  function attr(el, n) { return el.getAttribute(n); }
  function clean(t) { return String(t == null ? '' : t).replace(/[\s  ]+/g, ' ').trim(); }

  /* ---------------------------------------------------------------- money */
  /* The Liquid money filter's output for 123456 cents ("$1,234.56", "1.234,56 EUR", "¥1,235") gives the prefix, suffix,
     grouping and decimal marks and the number of decimals, exactly as Liquid prints them. */
  function spec(sample) {
    var sp = { pre: '$', suf: '', grp: ',', dec: '.', dp: 2 };
    var m = String(sample || '').match(/\d[\d.,'’\s  ]*\d/);
    if (!m) return sp;
    var num = m[0];
    sp.pre = sample.slice(0, m.index);
    sp.suf = sample.slice(m.index + num.length);
    var tail = num.match(/[^\d](\d+)$/);
    var head = num;
    sp.dp = 0;
    if (tail && tail[1].length !== 3) {
      sp.dp = tail[1].length;
      sp.dec = num.charAt(num.length - sp.dp - 1);
      head = num.slice(0, num.length - sp.dp - 1);
    }
    var g = head.match(/[^\d]/);
    sp.grp = g ? g[0] : '';
    return sp;
  }
  var SP = spec(cfg.krMoney || '$1,234.56');
  var SPC = cfg.krMoneyCur ? spec(cfg.krMoneyCur) : SP;

  function fmt(cents, sp) {
    sp = sp || SP;
    var neg = cents < 0;
    var c = Math.abs(Math.round(cents));
    var ip, fp = '';
    if (sp.dp === 0) {
      ip = String(Math.round(c / 100));
    } else {
      ip = String(Math.floor(c / 100));
      fp = String(c % 100);
      while (fp.length < 2) fp = '0' + fp;
      while (fp.length < sp.dp) fp += '0';
      fp = sp.dec + fp.slice(0, sp.dp);
    }
    if (sp.grp) ip = ip.replace(/\B(?=(\d{3})+(?!\d))/g, sp.grp);
    return (neg ? '-' : '') + sp.pre + ip + fp + sp.suf;
  }

  /* "$255.75", "$200" (Intelligems strips trailing .00), "$1,234.50", "1.234,56 EUR" -> cents, or null */
  function parse(text) {
    var m = String(text || '').match(/\d[\d.,'’\s  ]*/);
    if (!m) return null;
    var s = m[0].replace(/[^\d]+$/, '').replace(/['’\s  ]/g, '');
    var i = Math.max(s.lastIndexOf('.'), s.lastIndexOf(','));
    var ip = s, fp = '';
    if (i > -1 && s.length - i - 1 !== 3) { ip = s.slice(0, i); fp = s.slice(i + 1); }
    ip = ip.replace(/[.,]/g, '');
    if (!/^\d+$/.test(ip)) return null;
    fp = (fp + '00').slice(0, 2);
    return parseInt(ip, 10) * 100 + parseInt(fp, 10);
  }

  /* Liquid: price | times: (1 - pct/100) | ceil   and   price | times: (100 - pct) | divided_by: 100.0 | ceil.
     Integer arithmetic so the result matches Liquid's exact decimal maths to the cent. */
  function sale(p, pct) {
    var k = Math.round((100 - pct) * 100);
    return Math.ceil((p * k) / 10000);
  }

  /* Replace the visible text of an element without adding or removing nodes (characterData only). */
  function setText(el, text) {
    var w = d.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var n, first = null;
    while ((n = w.nextNode())) { if (/\S/.test(n.nodeValue)) { first = n; break; } }
    if (!first) { if (el.textContent !== text) el.textContent = text; return; }
    var cur = first.nodeValue;
    var want = cur.match(/^\s*/)[0] + text + cur.match(/\s*$/)[0];
    if (cur !== want) first.nodeValue = want;
    while ((n = w.nextNode())) { if (/\S/.test(n.nodeValue)) n.nodeValue = ''; }
  }

  /* ---------------------------------------------------------------- containers */
  function ownFirst(c, sel) {
    var list = c.querySelectorAll(sel);
    for (var i = 0; i < list.length; i++) if (!list[i].closest('[data-kr-ig-sink]')) return list[i];
    return null;
  }

  /* Intelligems looks up the variant on the price element itself first: variant 0 has no test, so it skips the element */
  function mark(el) {
    if (el.hasAttribute('data-kr-ig-mark')) return;
    el.setAttribute('data-kr-ig-mark', '');
    el.setAttribute('data-variant-id', '0');
    el.setAttribute('data-product-id', '0');
  }
  function unmark(el) {
    if (!el || !el.hasAttribute('data-kr-ig-mark')) return;
    el.removeAttribute('data-kr-ig-mark');
    el.removeAttribute('data-variant-id');
    el.removeAttribute('data-product-id');
  }

  function keepCompare(c, wasTxt) {
    var cmp = ownFirst(c, '.compare-at-price');
    if (!cmp) return;
    if (clean(cmp.textContent) !== clean(wasTxt)) setText(cmp, wasTxt);
    if (!cmp.hasAttribute('data-kr-ig-keep')) cmp.setAttribute('data-kr-ig-keep', '');
    if (cmp.classList.contains('ig-hidden')) cmp.classList.remove('ig-hidden');
    mark(cmp);
  }

  function forget(c) {
    if (c.hasAttribute('data-kr-ig-out')) c.removeAttribute('data-kr-ig-out');
    if (c.hasAttribute('data-kr-ig-p')) c.removeAttribute('data-kr-ig-p');
    unmark(ownFirst(c, '.price'));
    unmark(ownFirst(c, '.compare-at-price'));
  }

  /* safety net: if a container keeps changing, stop and leave it as Intelligems wrote it */
  var hits = typeof WeakMap === 'function' ? new WeakMap() : null;
  function tripped(c) {
    if (!hits) return false;
    var now = Date.now(), h = hits.get(c);
    if (!h || now - h.t > 3000) h = { t: now, n: 0 };
    h.n++;
    hits.set(c, h);
    if (h.n === 9) log('breaker: container keeps changing, left as Intelligems wrote it', c);
    return h.n > 8;
  }

  function derive(c, p, s, sp) {
    if (p === Number(attr(c, 'data-kr-base'))) return;
    var pid = attr(c, 'data-product-id') || '';
    var vid = attr(c, 'data-variant-id') || '';
    var list = [];
    function add(nodes) { for (var i = 0; i < nodes.length; i++) if (list.indexOf(nodes[i]) < 0) list.push(nodes[i]); }
    [c, c.closest('product-card'), c.closest('.pcard'), c.closest('[data-kr-ig-scope]')].forEach(function (sc) {
      if (sc) add(sc.querySelectorAll('[data-kr-ig-derive]'));
    });
    if (/^\d+$/.test(vid)) add(d.querySelectorAll('[data-kr-ig-derive][data-kr-ig-vid="' + vid + '"]'));
    list.forEach(function (el) {
      var ep = attr(el, 'data-kr-ig-pid'), ev = attr(el, 'data-kr-ig-vid');
      if (ep && ep !== pid) return;
      if (ev && ev !== vid) return;
      var kind = attr(el, 'data-kr-ig-derive');
      var n = parseFloat(attr(el, 'data-kr-ig-n')) || 1;
      var v;
      if (kind === 'save') v = p - s;
      else if (kind === 'div') v = attr(el, 'data-kr-ig-round') === 'ceil' ? Math.ceil(s / n) : Math.floor(s / n);
      else return;
      setText(el, (attr(el, 'data-kr-ig-tpl') || '{}').replace('{}', fmt(v, sp)));
    });
  }

  /* Sink mode, product outside the 15% display (tag no-sitewide-discount, gift card, not in the discount collections):
     show exactly what Intelligems wrote, as the live theme does. */
  function mirror(c, p, sp) {
    var sc = c.querySelector('[data-kr-ig-sink] .compare-at-price');
    var cp = sc && !sc.classList.contains('ig-hidden') ? parse(clean(sc.textContent)) : null;
    var nowEl = ownFirst(c, '[data-now]');
    var wasEl = ownFirst(c, '[data-was]');
    if (nowEl && clean(nowEl.textContent) !== clean(fmt(p, sp))) setText(nowEl, fmt(p, sp));
    if (wasEl) {
      var show = cp !== null && cp > p;
      if (show && clean(wasEl.textContent) !== clean(fmt(cp, sp))) setText(wasEl, fmt(cp, sp));
      if (wasEl.hidden === show) wasEl.hidden = !show;
    }
    if (attr(c, 'data-kr-ig-p') !== String(p)) c.setAttribute('data-kr-ig-p', String(p));
  }

  function proc(c) {
    if (c.hasAttribute('data-kr-vol')) return;
    var pct = parseFloat(attr(c, 'data-kr-pct'));
    var disc = attr(c, 'data-kr-disc') === '1' && pct > 0 && pct < 100;
    var mode = attr(c, 'data-kr-ig') === 'sink' ? 'sink' : 'inplace';
    var sp = c.hasAttribute('data-kr-cur') ? SPC : SP;
    var p;

    if (mode === 'sink') {
      var sink = c.querySelector('[data-kr-ig-sink] .price');
      if (!sink) return;
      var raw = clean(sink.textContent);
      if (!raw) return; /* the sink is rendered empty: empty means Intelligems has not written */
      p = parse(raw);
      if (p === null || !(p > 0)) return;
      if (!disc) { mirror(c, p, sp); return; }
    } else {
      /* inplace: Intelligems' own write is already the true price for products outside the 15% display */
      if (!disc) return;
      var el = ownFirst(c, '.price');
      if (!el) return;
      var txt = clean(el.textContent);
      var out = attr(c, 'data-kr-ig-out');
      if (out !== null && txt === out) {
        var kp = Number(attr(c, 'data-kr-ig-p'));
        if (kp > 0) keepCompare(c, fmt(kp, sp));
        return;
      }
      p = parse(txt);
      if (p === null || !(p > 0)) return;
      /* the Liquid render (first paint, section re-render, Horizon morph): never discount it again. Intelligems hides
         the compare-at whenever it writes a price, so a test price that happens to equal the Liquid sale price (a -15%
         arm) is still recognised as a test price. */
      var cmpEl = ownFirst(c, '.compare-at-price');
      if (p === Number(attr(c, 'data-kr-now')) && !(cmpEl && cmpEl.classList.contains('ig-hidden'))) { forget(c); return; }
    }

    var s = sale(p, pct);
    var wasTxt = fmt(p, sp);
    var nowTxt = fmt(s, sp);
    if (mode === 'sink') {
      var nowEl = ownFirst(c, '[data-now]');
      var wasEl = ownFirst(c, '[data-was]');
      if (nowEl && clean(nowEl.textContent) !== clean(nowTxt)) setText(nowEl, nowTxt);
      if (wasEl && clean(wasEl.textContent) !== clean(wasTxt)) setText(wasEl, wasTxt);
    } else {
      if (tripped(c)) return;
      var pe = ownFirst(c, '.price');
      setText(pe, nowTxt);
      c.setAttribute('data-kr-ig-out', clean(nowTxt));
      mark(pe);
      keepCompare(c, wasTxt);
    }
    if (attr(c, 'data-kr-ig-p') !== String(p)) {
      c.setAttribute('data-kr-ig-p', String(p));
      log(mode, 'product', attr(c, 'data-product-id'), 'variant', attr(c, 'data-variant-id'), 'P', p, '->', wasTxt, nowTxt);
    }
    derive(c, p, s, sp);
  }

  function scan() {
    var list = d.querySelectorAll(SEL);
    for (var i = 0; i < list.length; i++) proc(list[i]);
  }

  /* ---------------------------------------------------------------- anti-flicker (sink mode) */
  var afOn = false;
  var style = d.createElement('style');
  style.id = 'kr-ig-style';
  style.textContent =
    '.kr-ig-af [data-kr-ig="sink"][data-kr-disc="1"] [data-now],' +
    '.kr-ig-af [data-kr-ig="sink"][data-kr-disc="1"] [data-was],' +
    '.kr-ig-af [data-kr-ig-derive]{opacity:0!important}';
  (d.head || root).appendChild(style);
  function af() {
    var on = !!d.getElementById('ig-af-price') && Date.now() - T0 < AF_MAX_MS;
    if (on !== afOn) { afOn = on; root.classList.toggle('kr-ig-af', on); }
  }
  setTimeout(af, AF_MAX_MS + 50);

  /* ---------------------------------------------------------------- observer */
  function onMut(muts) {
    var todo = [];
    function push(c) { if (todo.indexOf(c) < 0) todo.push(c); }
    for (var i = 0; i < muts.length; i++) {
      var m = muts[i], t = m.target;
      if (m.type === 'attributes') {
        /* Intelligems adds ig-hidden when it writes (also on later passes): a written container is (re)processed, which
           keeps the struck price visible where a test price is shown, and catches a write that only hid the compare-at */
        if (t.nodeType === 1 && t.classList.contains('ig-hidden')) {
          var kc = t.closest(SEL);
          if (kc) push(kc);
        }
        continue;
      }
      var el = t.nodeType === 1 ? t : t.parentElement;
      if (el) { var c = el.closest(SEL); if (c) push(c); }
      var added = m.addedNodes;
      for (var j = 0; j < added.length; j++) {
        var a = added[j];
        if (a.nodeType !== 1) continue;
        if (a.matches(SEL)) push(a);
        var inner = a.querySelectorAll(SEL);
        for (var k = 0; k < inner.length; k++) push(inner[k]);
      }
    }
    af();
    for (var x = 0; x < todo.length; x++) {
      try { proc(todo[x]); } catch (e) { if (window.console) console.warn('[krig]', e); }
    }
  }

  try {
    new MutationObserver(onMut).observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  } catch (e) { /* very old browser: prices stay as Intelligems writes them */ }
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', scan);
  window.addEventListener('load', scan);

  /* ---------------------------------------------------------------- API for theme JS that re-renders prices */
  function escAttr(v) { return String(v == null ? '' : v).replace(/[&"<>]/g, function (ch) { return { '&': '&amp;', '"': '&quot;', '<': '&lt;', '>': '&gt;' }[ch]; }); }
  window.KRIG = {
    version: '2026-09-25',
    fmt: function (cents, withCurrency) { return fmt(cents, withCurrency ? SPC : SP); },
    parse: parse,
    sale: sale,
    scan: scan,
    /* Same markup as snippets/kr-price.liquid (sink mode). o = { vid, pid, disc, pct, base, inner } */
    wrap: function (o) {
      return '<div ref="priceContainer" class="kr-pc" style="display:contents" data-kr-ig="sink"' +
        ' data-variant-id="' + escAttr(o.vid) + '" data-product-id="' + escAttr(o.pid) + '"' +
        ' data-kr-disc="' + (o.disc ? 1 : 0) + '" data-kr-pct="' + escAttr(o.pct || 0) + '" data-kr-base="' + escAttr(o.base || 0) + '">' +
        (o.inner || '') +
        '<span data-kr-ig-sink hidden style="display:none!important"><span class="compare-at-price"></span><span class="price"></span></span></div>';
    }
  };
})();
